import { useState } from "react";
import { ModalV3 } from "src/components/ModalV3/ModalV3.tsx";
import { Button } from "src/components/Button/Button.tsx";
import { Input } from "src/components/Input/Input.tsx";
import { Flex } from "src/components/Flex.tsx";
import { useResourceFormTranslation } from "src/components/Form/hooks/useResourceFormTranslation.ts";
import type { StoreSupplierAgreementPriceRequestBody } from "@exonn/js-sdk";

export type PriceDialogMode = "create" | "close";

type Props = {
  mode: PriceDialogMode;
  isSaving: boolean;
  errors: Record<string, string[]>;
  onSubmit: (body: Partial<StoreSupplierAgreementPriceRequestBody>) => void;
  onClose: () => void;
};

const today = () => new Date().toISOString().slice(0, 10);

// Новая ступень цены или закрытие действующей (только дата окончания).
export const PriceDialog = ({
  mode,
  isSaving,
  errors,
  onSubmit,
  onClose,
}: Props) => {
  const { t } = useResourceFormTranslation();
  const [minQuantity, setMinQuantity] = useState("1");
  const [unitPrice, setUnitPrice] = useState("");
  const [validFrom, setValidFrom] = useState(today());
  const [validTo, setValidTo] = useState("");

  const error = (field: string) => errors[field]?.[0];

  const submit = () => {
    if (mode === "close") {
      onSubmit({ valid_to: validTo || null });
      return;
    }
    onSubmit({
      min_quantity: minQuantity,
      unit_price: unitPrice,
      valid_from: validFrom,
      valid_to: validTo || null,
    });
  };

  return (
    <ModalV3 onClose={onClose}>
      <ModalV3.Positioner>
        <ModalV3.Content>
          <ModalV3.CloseTrigger />
          <ModalV3.Header>
            <ModalV3.Title>
              {mode === "create"
                ? t("prices.dialog.create-title")
                : t("prices.dialog.close-title")}
            </ModalV3.Title>
          </ModalV3.Header>
          <ModalV3.Body style={{ width: 460 }}>
            <Flex flexDirection={"column"} gap={"15px"}>
              {mode === "create" && (
                <>
                  <Input
                    label={t("prices.min-quantity")}
                    type={"number"}
                    min={0}
                    value={minQuantity}
                    onChange={(event) => setMinQuantity(event.target.value)}
                    error={error("min_quantity")}
                  />
                  <Input
                    label={t("prices.unit-price")}
                    type={"number"}
                    min={0}
                    value={unitPrice}
                    onChange={(event) => setUnitPrice(event.target.value)}
                    error={error("unit_price")}
                  />
                  <Input
                    label={t("prices.valid-from")}
                    type={"date"}
                    min={today()}
                    value={validFrom}
                    onChange={(event) => setValidFrom(event.target.value)}
                    error={error("valid_from")}
                    helperText={t("prices.dialog.valid-from-helper")}
                  />
                </>
              )}
              <Input
                label={t("prices.valid-to")}
                type={"date"}
                min={today()}
                value={validTo}
                onChange={(event) => setValidTo(event.target.value)}
                error={error("valid_to")}
                helperText={
                  mode === "close"
                    ? t("prices.dialog.close-helper")
                    : t("prices.dialog.valid-to-helper")
                }
              />
            </Flex>
          </ModalV3.Body>
          <ModalV3.Footer>
            <ModalV3.FooterButtons>
              <Button variant={"outlined"} onClick={onClose}>
                {t("prices.dialog.cancel")}
              </Button>
              <Button
                variant={"filled"}
                onClick={submit}
                disabled={
                  isSaving ||
                  (mode === "create" && unitPrice === "") ||
                  (mode === "close" && validTo === "")
                }
              >
                {t("prices.dialog.save")}
              </Button>
            </ModalV3.FooterButtons>
          </ModalV3.Footer>
        </ModalV3.Content>
      </ModalV3.Positioner>
    </ModalV3>
  );
};
